import React, { useState, useEffect, useRef } from "react";
import { fetchNumberInRange } from "../services/api";
import { funFacts } from "../data/staticData";

interface NumberRangeGameProps {
  selectedCountry: string;
  minNum: number;
  maxNum: number;
}

const NumberRangeGame: React.FC<NumberRangeGameProps> = ({ selectedCountry, minNum, maxNum }) => {
  const [displayNumber, setDisplayNumber] = useState("");
  const [spokenUrl, setSpokenUrl] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [message, setMessage] = useState("");
  const [correctCount, setCorrectCount] = useState(0);
  const [nextData, setNextData] = useState<{ rnum: string, spokenurl: string } | null>(null);
  const [readyForNext, setReadyForNext] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const funFactKey = maxNum === 10 ? "num_range_0_10" : maxNum === 100 ? "num_range_0_100" : null;
  const funFact = funFactKey && selectedCountry in funFacts[funFactKey] 
  ? funFacts[funFactKey][selectedCountry as keyof typeof funFacts[typeof funFactKey]] 
  : null;

  const [showModal, setShowModal] = useState(false); // Modal visibility state

  const loadNumber = async () => {
    const data = await fetchNumberInRange(minNum, maxNum, selectedCountry);
    setDisplayNumber(data.rnum);
    setSpokenUrl(data.spokenurl);
  };

  // Prefetch the next question in the background
  const prefetchNext = async () => {
    const next = await fetchNumberInRange(minNum, maxNum, selectedCountry);
    setNextData(next);
  };

  // Initialize by loading the first question and prefetching the next one
  useEffect(() => {
    console.log("first useEffect");
    loadNumber();
    setTimeout(() => {
      prefetchNext(); // Prefetch the next question after 1s
    }, 1000);    
    
  }, [minNum, maxNum, selectedCountry]);

  // Update current question only when `readyForNext` is true and `nextData` is available
  useEffect(() => {
    if (readyForNext && nextData) {
      setDisplayNumber(nextData.rnum);
      setSpokenUrl(nextData.spokenurl);
      setNextData(null); // Clear nextData after setting it as the current question
      setReadyForNext(false); // Reset readiness for future correct answers
      prefetchNext(); // Start prefetching the following question
    }
  }, [nextData, readyForNext]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (inputValue == displayNumber) {
      setMessage("Correct");
      setCorrectCount((prev) => prev + 1);
      setInputValue("");

      setTimeout(() => {
        setMessage("");
        setReadyForNext(true);
      }, 1000);
    } else {
      setMessage("Incorrect");
    }
  };

  useEffect(() => {
    if (spokenUrl && audioRef.current) {
      audioRef.current.load();
      audioRef.current.play();
    }
  }, [spokenUrl]);

  return (
    <div>
      <div className="game-header">
        <h2>Number Range Quiz ({minNum}-{maxNum})</h2>
        {funFact && ( // Show the question mark icon only if a fun fact exists
          <button className="info-btn" onClick={() => setShowModal(true)}>
            ❓
          </button>
        )}
      </div>
      
      <p>Correct answers: {correctCount}</p>

      {spokenUrl && (
        <div>
          <audio ref={audioRef} controls>
            <source src={spokenUrl} type="audio/mpeg" />
            Your browser does not support the audio element.
          </audio>
          <form onSubmit={handleSubmit}>
            <input 
              type="number" 
              value={inputValue} 
              onChange={(e) => setInputValue(e.target.value)} 
              required
            />
            <button type="submit">Submit</button>
          </form>
        </div>
      )}
      {message && <p>{message}</p>}
      {showModal && funFact && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Fun Facts</h3>
            <div dangerouslySetInnerHTML={{ __html: funFact }} />
            <button onClick={() => setShowModal(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NumberRangeGame;
